/**
 * Suprime erros de extensões do navegador (MetaMask, etc)
 * que não afetam o funcionamento do CMS
 * 
 * VERSÃO ULTRA-AGRESSIVA com múltiplas camadas de proteção
 */

export function suppressExtensionErrors() {
  if (typeof window === 'undefined') return;

  // Salvar os handlers originais
  const originalError = window.onerror;
  const originalUnhandledRejection = window.onunhandledrejection;
  const originalConsoleError = console.error;
  const originalConsoleWarn = console.warn;

  // Palavras-chave de erros a ignorar (EXPANDIDA)
  const ignoredKeywords = [
    'MetaMask',
    'metamask',
    'ethereum',
    'chrome-extension',
    'moz-extension',
    'safari-extension',
    'Failed to connect',
    'extension not found',
    'web3',
    'ethers',
    'inpage.js',
    'inpage',
    'scripts/inpage',
    '__REACT_DEVTOOLS',
    'Wallet',
    'wallet',
    'connect wallet',
    'nkbihfbeogaeaoehlefnkodbefgpgknn', // ID específico da extensão MetaMask
    // Erros comuns em alguns smartphones/navegadores que não devem quebrar o CMS
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications',
    'Script error',
    'insertBefore',
    'removeChild',
    'NotFoundError',
    'Failed to execute'
  ];

  // Função OTIMIZADA para verificar se deve ignorar o erro
  const shouldIgnoreError = (message: string): boolean => {
    if (!message) return false;
    const lowerMessage = String(message).toLowerCase();
    // Usar indexOf ao invés de includes para melhor performance
    for (let i = 0; i < ignoredKeywords.length; i++) {
      if (lowerMessage.indexOf(ignoredKeywords[i].toLowerCase()) !== -1) {
        return true;
      }
    }
    return false;
  };

  // Função para extrair mensagem de diferentes tipos de erro
  const extractErrorMessage = (error: any): string => {
    if (!error) return '';
    if (typeof error === 'string') return error;
    if (error.message) return error.message;
    if (error.reason) return error.reason;
    if (error.stack) return error.stack;
    return String(error);
  };

  // Interceptar window.onerror
  window.onerror = function(message, source, lineno, colno, error) {
    const errorMessage = typeof message === 'string' ? message : String(message);
    const errorStack = error?.stack || '';
    
    if (shouldIgnoreError(errorMessage) || shouldIgnoreError(source || '') || shouldIgnoreError(errorStack)) {
      // Suprime completamente sem mensagem no console
      return true;
    }

    // Chamar o handler original
    if (originalError) {
      return originalError.call(window, message, source, lineno, colno, error);
    }
    return false;
  };

  // Interceptar unhandled promise rejections
  window.onunhandledrejection = function(event) {
    const reason = event.reason;
    const message = extractErrorMessage(reason);
    
    if (shouldIgnoreError(message)) {
      // Suprime completamente sem mensagem no console
      event.preventDefault();
      return;
    }

    // Chamar o handler original
    if (originalUnhandledRejection) {
      originalUnhandledRejection.call(window, event);
    }
  };

  // Interceptar console.error AGRESSIVAMENTE
  console.error = function(...args: any[]) {
    const message = args.map(arg => extractErrorMessage(arg)).join(' ');
    
    if (shouldIgnoreError(message)) {
      // Suprime completamente - não mostra nada
      return;
    }

    // Chamar o console.error original
    originalConsoleError.apply(console, args);
  };

  // Interceptar console.warn AGRESSIVAMENTE
  console.warn = function(...args: any[]) {
    const message = args.map(arg => extractErrorMessage(arg)).join(' ');
    
    if (shouldIgnoreError(message)) {
      // Suprime completamente - não mostra nada
      return;
    }

    // Chamar o console.warn original
    originalConsoleWarn.apply(console, args);
  };

  // ADICIONAR: Event listeners adicionais com useCapture
  window.addEventListener('error', (e: ErrorEvent) => {
    const message = extractErrorMessage(e.error || e.message);
    const filename = e.filename || '';
    
    if (shouldIgnoreError(message) || shouldIgnoreError(filename)) {
      e.preventDefault();
      e.stopPropagation();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();
      return false;
    }
  }, true); // useCapture = true

  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    const message = extractErrorMessage(e.reason);
    
    if (shouldIgnoreError(message)) {
      e.preventDefault();
      e.stopPropagation();
      if ((e as any).stopImmediatePropagation) (e as any).stopImmediatePropagation();
      return false;
    }
  }, true); // useCapture = true

  // ADICIONAR: Proxy para console se disponível (proteção extra)
  if (typeof Proxy !== 'undefined') {
    try {
      (['error', 'warn'] as const).forEach((method) => {
        const original = console[method];
        (console as any)[method] = new Proxy(original, {
          apply: (target, thisArg, args) => {
            const message = args.map(arg => extractErrorMessage(arg)).join(' ');
            if (shouldIgnoreError(message)) {
              return;
            }
            return Reflect.apply(target, thisArg, args);
          }
        });
      });
    } catch (e) {
      // Se Proxy falhar, já temos os overrides básicos
    }
  }
}

// Executar imediatamente ao carregar o módulo
if (typeof window !== 'undefined') {
  suppressExtensionErrors();
}

